import React, { Fragment } from 'react'
import { connect } from 'react-redux'
import compose from 'recompose/compose'
import PositionalMediaPlayer from '../../components/2d/positionalMedia/PositionalMediaPlayer'
import { INTERACTIVE_ELEMENT_OPTIONS } from '../../constants'
import { lookupElement } from '../../methods/lookupElement'
import { get2DContextCoordinates } from '../../methods/normalizeData'
import withLifeCyle from 'recompose/lifecycle'
import { registerPositionalMedia, unregisterPositionalMedia, setPositionalMediaStatus, MediaStates } from '../../'
import { playAudio, stopAudio, AUDIO_TOUR, AUDIO_PM } from 'aria-viewer/audiomanager/audioManagerActions'
import { WithForwardRef } from 'aria-viewer/common'
import { handleProgress } from '../../observables/positionalMediaObservable'
import withStateHandlers from 'recompose/withStateHandlers'
import { ErrorBoundary } from 'aria-viewer/common'

const calculateCornerPoints = (x, y, width, height) => {
    var nw = { x: x, y: y, z: 0 }
    var ne = { x: x + width, y: y, z: 0 }
    var sw = { x, y: y + height, z: 0 }
    var se = { x: x + width, y: y + height, z: 0 }

    return {
        nw,
        ne,
        sw,
        se,
    }
}

const mapStateToProps = (state, { id, videoWidth, videoHeight }) => {
    const viewportWidth = state.getIn(['device', 'viewportWidth'])
    const viewportHeight = state.getIn(['device', 'viewportHeight'])
    const photoWidth = state.getIn(['tour', 'mainMedia', 'width'])
    const photoHeight = state.getIn(['tour', 'mainMedia', 'height'])

    const calculatedWidth = viewportWidth > photoWidth ? photoWidth : viewportWidth
    const calculatedHeight = viewportHeight > photoHeight ? photoHeight : viewportHeight

    const { left, top, width: boundingRectWidth, height: boundingRectHeight } = state.getIn([
        'tour',
        'mainMedia',
        'photoPlayer',
    ])
    const hasPositionalMediaPlayer =
        (state.getIn(['interactiveElements', 'positionalMediaPlayers']) || {})[id] !== undefined

    const el = lookupElement({ id, type: 'positionalMedia', state, toJS: true })
    const { positionalMediaItems, position, autoplay: autoPlay } = el

    const mediaItem = lookupElement({ type: 'positionalMediaItems', id: positionalMediaItems[0], state, toJS: true })

    const video = lookupElement({
        type: 'videoMain',
        id: mediaItem.videoMain[0],
        state,
        toJS: true,
    })

    const rendition = lookupElement({
        type: 'renditions',
        id: video.renditions[0],
        state,
        toJS: true,
    })

    const { url: src } = rendition
    const width = videoWidth || rendition.width
    const height = videoHeight || rendition.height

    let points = []

    const { x, y, z } = get2DContextCoordinates(position)(calculatedWidth, calculatedHeight)

    let leftOffset = left,
        topOffset = top,
        heightRatio = 1 // heightRatio is used for properly offsetting portrait photos

    // For portrait images we need to offset the width to adjust for the difference between the bounding rect height
    // and image natural height
    if (calculatedWidth < calculatedHeight) {
        heightRatio = calculatedHeight / boundingRectHeight

        const currImageWidth = calculatedWidth / heightRatio

        const widthOffset = (boundingRectWidth - currImageWidth) / 2

        leftOffset += widthOffset
    }

    if (position.length == 1) {
        const { nw, ne, sw, se } = calculateCornerPoints(
            x / heightRatio + leftOffset,
            y / heightRatio + topOffset,
            width,
            height
        )

        points = [nw, ne, sw, se]
    } else {
        points = position.map(pos => {
            const { x, y, z } = get2DContextCoordinates([pos])(calculatedWidth, calculatedHeight)

            return { x: x / heightRatio + leftOffset, y: y / heightRatio + topOffset, z: z }
        })
    }

    return {
        ...el,
        width,
        height,
        points,
        x,
        y,
        z,
        src,
        autoPlay,
        hasPositionalMediaPlayer,
        preload: 'metadata',
        poster: video.largeurl,
        zIndex: 3,
    }
}

const mapDispatchToProps = dispatch => ({
    registerPositionalMedia: positionalMedia => {
        dispatch(registerPositionalMedia(positionalMedia))
    },

    unregisterPositionalMedia: id => {
        dispatch(unregisterPositionalMedia(id))
    },

    setPositionalMediaStatus: (id, playState) => {
        dispatch(setPositionalMediaStatus(id, playState))
    },

    playPositionalMediaAudio: () => {
        dispatch(playAudio(AUDIO_PM))
    },

    stopPositionalMediaAudio: () => {
        dispatch(stopAudio(AUDIO_PM))
    },

    stopTourAudio: () => {
        dispatch(stopAudio(AUDIO_TOUR))
    },

    playTourAudio: () => {
        dispatch(playAudio(AUDIO_TOUR))
    },
})

const mergeProps = (stateProps, dispatchProps, ownProps) => ({
    ...stateProps,
    ...dispatchProps,
    ...ownProps,

    onPause: _ => {
        const { id } = ownProps
        dispatchProps.setPositionalMediaStatus(id, MediaStates.PLAY)
        dispatchProps.stopPositionalMediaAudio()
        dispatchProps.playTourAudio()
    },

    onPlay: _ => {
        const { id } = ownProps
        dispatchProps.setPositionalMediaStatus(id, MediaStates.PAUSE)
        dispatchProps.playPositionalMediaAudio()
        dispatchProps.stopTourAudio()
    },

    onEnded: _ => {
        const { id } = ownProps
        dispatchProps.setPositionalMediaStatus(id, MediaStates.REPLAY)
        dispatchProps.stopPositionalMediaAudio()
        dispatchProps.playTourAudio()
    },

    onTimeUpdate: e => {
        const { id } = ownProps
        const { currentTime, duration } = e.target
        const progress = currentTime / duration

        handleProgress({
            id,
            progress,
        })
    },
})

const renderWithPlayer = Component => ({ contextType, ...props }) =>
    contextType == INTERACTIVE_ELEMENT_OPTIONS.CONTEXT.context2D ? (
        <ErrorBoundary>
            <Component ref={React.createRef()} contextType={contextType} {...props} />
        </ErrorBoundary>
    ) : (
        <Fragment>{props.children}</Fragment>
    )

const lifecycle = withLifeCyle({
    componentDidUpdate: function() {
        const { id, registerPositionalMedia, hasPositionalMediaPlayer, forwardRef } = this.props

        if (!hasPositionalMediaPlayer) {
            const video = forwardRef.current

            this.positionalMediaPlayer = {
                arg: {
                    id,
                },

                play: _ => (video.paused ? video.play() : video.pause()),

                pause: _ => video.pause(),

                isPlaying: _ => !video.paused,

                unload: _ => {
                    video.pause()
                    video.src = ''
                    video.load()
                },
            }

            registerPositionalMedia(this.positionalMediaPlayer)
        }
    },

    componentWillUnmount: function() {
        this.positionalMediaPlayer && this.positionalMediaPlayer.unload && this.positionalMediaPlayer.unload()
        this.positionalMediaPlayer = null
    },
})

const state = withStateHandlers(
    ({ videoWidth = undefined, videoHeight = undefined }) => ({ videoWidth, videoHeight }),
    {
        onLoadedMetadata: _ => e => {
            const video = e.target
            const { videoWidth, videoHeight } = video

            return { videoWidth, videoHeight }
        },
    }
)

export default compose(
    state,
    connect(mapStateToProps, mapDispatchToProps, mergeProps),
    renderWithPlayer,
    WithForwardRef,
    lifecycle
)(PositionalMediaPlayer)
